/**
 * @fileoverview Reglas de enrutamiento de flujos (chatbots) entre el monolito de Mercately
 * y el microservicio de chatbots.
 *
 * Cada canal lo atiende un solo motor:
 *   - Monolito (Rails): WhatsApp (retailers no migrados), Messenger, Instagram.
 *   - Microservicio: TikTok, Messenger/Instagram Multipágina y WhatsApp de los retailers
 *     migrados (global setting `inbox_microservice_whatsapp_messages_retailers`).
 *
 * Por eso un flujo no puede mezclar canales de ambos motores, y el destino de un flujo
 * nuevo se decide con los canales seleccionados + la migración del retailer.
 *
 * @module chatbotEngineRouting
 */

/** Clave de WhatsApp dentro de `retailer_info.inbox_microservice_platforms`. */
export const MS_WHATSAPP_PLATFORM_KEY = 'whatsapp_messages';
export const MS_FACEBOOK_PLATFORM_KEY = 'facebook_messages';
export const MS_INSTAGRAM_PLATFORM_KEY = 'instagram_messages';

/**
 * ¿El WhatsApp de este retailer lo atiende el microservicio?
 * @param {Object|null|undefined} retailerInfo `retailer_info` del serializer de Mercately
 * @returns {boolean}
 */
export function isMsWhatsappRetailer(retailerInfo) {
  return retailerInfo?.inbox_microservice_platforms?.[MS_WHATSAPP_PLATFORM_KEY] === true;
}

/**
 * ¿El Facebook Messenger de este retailer lo atiende el microservicio?
 * @param {Object|null|undefined} retailerInfo `retailer_info` del serializer de Mercately
 * @returns {boolean}
 */
export function isMsFacebookRetailer(retailerInfo) {
  return retailerInfo?.inbox_microservice_platforms?.[MS_FACEBOOK_PLATFORM_KEY] === true;
}

/**
 * ¿El Instagram de este retailer lo atiende el microservicio?
 * @param {Object|null|undefined} retailerInfo `retailer_info` del serializer de Mercately
 * @returns {boolean}
 */
export function isMsInstagramRetailer(retailerInfo) {
  return retailerInfo?.inbox_microservice_platforms?.[MS_INSTAGRAM_PLATFORM_KEY] === true;
}

/**
 * Canales que atiende el microservicio para este retailer.
 * @param {Object} chatbot payload/estado del flujo (whatsapp_enabled, tiktok_enabled, ...)
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function hasMicroserviceChannels(chatbot, retailerInfo) {
  if (
    chatbot?.tiktok_enabled
    || chatbot?.messenger_multipage_enabled
    || chatbot?.instagram_multipage_enabled
  ) {
    return true;
  }

  if (chatbot?.whatsapp_enabled && isMsWhatsappRetailer(retailerInfo)) return true;
  if (chatbot?.messenger_enabled && isMsFacebookRetailer(retailerInfo)) return true;
  if (chatbot?.instagram_enabled && isMsInstagramRetailer(retailerInfo)) return true;

  return false;
}

/**
 * Canales que atiende el monolito para este retailer.
 * @param {Object} chatbot payload/estado del flujo
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function hasMercatelyChannels(chatbot, retailerInfo) {
  if (chatbot?.whatsapp_enabled && !isMsWhatsappRetailer(retailerInfo)) return true;
  if (chatbot?.messenger_enabled && !isMsFacebookRetailer(retailerInfo)) return true;
  if (chatbot?.instagram_enabled && !isMsInstagramRetailer(retailerInfo)) return true;

  return false;
}

/**
 * ¿El retailer está registrado en el microservicio de inbox para alguna plataforma?
 * (GlobalSetting `inbox_microservice_*_retailers`).
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function isRetailerInMicroservice(retailerInfo) {
  const msPlatforms = retailerInfo?.inbox_microservice_platforms;
  if (!msPlatforms) return false;
  return Object.values(msPlatforms).some((enabled) => enabled === true);
}

/**
 * Determina si el flujo pertenece al microservicio de chatbots.
 * Cada flujo corre en el motor correspondiente a sus canales:
 *   - Microservicio: si `isFromMicroservice` es true, o tiene algún canal atendido
 *     por el microservicio (TikTok, multipágina, o WhatsApp/Facebook/Instagram migrado para este retailer).
 *   - Monolito (Rails): si todos sus canales activos son atendidos por el monolito.
 *
 * @param {Object} [chatbot]
 * @param {Object|null|undefined} retailerInfo
 * @returns {boolean}
 */
export function isMicroserviceBotOrRetailer(chatbot, retailerInfo) {
  if (chatbot?.isFromMicroservice || hasMicroserviceChannels(chatbot, retailerInfo)) {
    return true;
  }
  return false;
}

